/// 入力エラーレスポンス関連
export const ValidateErrorTags = {
  NotMachPassword: "NotMachPassword",
  PasswordIsInvalid: "PasswordIsInvalid",
  NotEqualPass: "NotEqualPass",
  Required: "required",
  Email: "email",
  GTfield: "gtfield",
  Lte: "lte",
  ExistsSameName: "ExistsSameName",
  ExistsSameMail: "ExistsSameMail",
} as const;
export type ValidateErrorTags =
  typeof ValidateErrorTags[keyof typeof ValidateErrorTags];

// エラーレスポンスモデル
export interface IErrorResponse {
  failedField: string;
  tag: ValidateErrorTags;
  value: string;
}

//// APIモデル
export enum Roles {
  None,
  Admin,
}
// ユーザモデル
export interface IUser {
  id?: number;
  email: string;
  name: string;
  role: Roles;
}
// 契約ステータス
export enum ContractStatus {
  Enabled,
  Disabled,
}
export enum ContractType {
  Trial,
  Agreement,
}
// 契約モデル
export interface IContract {
  id?: number;
  companyName: string;
  optionName: string;
  plan: number;
  signed: number;
  expire: number;
  describe: string;
  users: IUser[];
  status: ContractStatus;
  type: ContractType;
}
// テンプレートセットモデル
export interface ITemplateSet {
  id?: number;
  frames: ITemplateImage[];
  thumbnail: string;
  tags: IAttribute[];
  describe: string;
  deleteFlg: boolean;
}
// テンプレート用画像モデル
export interface ITemplateImage {
  id: number;
  order: number;
  templateID?: number;
  balloonOff: string;
  balloonOn: string;
  author: IAuthor;
}
// サンプルセットモデル
export interface ISampleSet {
  id?: number;
  frames: ISampleFrame[];
  thumbnail: string;
  tags: IAttribute[];
  describe: string;
}
// サンプルフレームモデル
export interface ISampleFrame {
  id?: number;
  sampleID?: number;
  order: number;
  isBalloonOn: boolean;
  templateImageID: number;
  items: IEditableObject[];
}
// 属性モデル
export interface IAttribute {
  id: number;
  name: string;
}
// 属性グループモデル
export interface IAttributeGroup {
  id: number;
  name: string;
  attributes: IAttribute[];
}
// 作者モデル
export interface IAuthor {
  id: number;
  name: string;
  isFamous: boolean;
}
// キャンペーンモデル
export interface ICampaign {
  id: number;
  bunnerPath: string;
  filters: ITemplateFilter[];
}
// テンプレートフィルタモデル
export interface ITemplateFilter {
  campaignID: number;
  authorID?: number;
  attributeID?: number;
}
// 素材の公開範囲
export enum MaterialRights {
  Public,
  User,
}
// 素材の種類
export enum MaterialTypes {
  Image,
  Balloon,
}
// 素材情報モデル
export interface IImageMaterial {
  id?: number;
  name: string;
  path: string;
  right: MaterialRights;
  type: MaterialTypes;
  createuser?: IUser;
}
// 編集オブジェクトモデル
export interface IEditableObject {
  id?: number;
  key: string; // 新規保存時にUndoデータを壊したくないためだけに存在するユニークな値
  editFrameID?: number;
  x: number;
  y: number;
  scale: number;
  rotate: number;
}
// テキストオブジェクトモデル
export interface ITextObject extends IEditableObject {
  content: string;
  isVertical: boolean;
  lineHeight: number;
}
// 画像オブジェクトモデル
export interface IImageObject extends IEditableObject {
  image: IImageMaterial;
}
// 編集フレームモデル
export interface IEditFrame {
  id?: number;
  editDataID?: number;
  order: number;
  isBalloonOn: boolean;
  templateImageID: number;
  items: IEditableObject[];
}
// 編集セットモデル
export interface IEditSet {
  id?: number;
  editDataID?: number;
  thumbnailPath?: string;
  thumbnailImage: string;
}
// 編集データモデル
export interface IEditData {
  id?: number;
  templateID?: number;
  title: string;
  editset?: IEditSet;
  frames: IEditFrame[];
}
// 初期編集データ
export const emptyEditData = (template: ITemplateSet) => {
  return {
    id: undefined,
    templateID: template.id,
    title: "無題",
    editset: undefined,
    frames: Array.from(template.frames).map<IEditFrame>((f) => ({
      order: f.order,
      isBalloonOn: true,
      templateImageID: f.id,
      items: [],
    })),
  } as IEditData;
};
export const emptyEditSet = () => {
  return {
    id: undefined,
    editDataID: undefined,
    thumbnailPath: undefined,
    thumbnailImage: "",
  } as IEditSet;
};

export const makeTemplateSet = (
  editData: IEditData,
  templateSets: ITemplateSet[]
): ITemplateSet => {
  const tmplFrames = makeTemplteImages(editData, templateSets);
  if (tmplFrames.length !== 4) {
    // 4コマ分のテンプレートがなければテンプレートなし(削除されたテンプレートとして固定画像表示)
    return makeNoImageTemplateSet();
  }
  return {
    id: undefined,
    frames: tmplFrames,
    thumbnail: editData.editset?.thumbnailPath ?? "",
    tags: [],
    describe: "",
    deleteFlg: false
  };
};

export const makeNoImageTemplateSet = (): ITemplateSet => {
  return {
    id: undefined,
    frames: makeNoImageTemplteImages(),
    thumbnail: consts.noImageThumbnail,
    tags: [],
    describe: "",
    deleteFlg: false
  };
};

export const isInvalidTemplate = (
  templateSet: ITemplateSet | undefined
): boolean => {
  if (templateSet !== undefined) {
    return templateSet.frames.some((x) => x.id === -1);
  }
  return true;
};

export const makeTemplteImages = (
  editData: IEditData,
  templateSets: ITemplateSet[]
): ITemplateImage[] => {
  const tmplFrames = editData.frames.map((editFrame) => {
    let tmplFrame: ITemplateImage | undefined;
    for (const template of templateSets) {
      tmplFrame = template.frames.find(
        (x) => x.id == editFrame.templateImageID
      );
      if (tmplFrame !== undefined) break;
    }
    if (tmplFrame === undefined) return;
    return {
      id: tmplFrame.id,
      order: editFrame.order,
      templateID: editData.templateID ?? undefined,
      balloonOff: tmplFrame.balloonOff,
      balloonOn: tmplFrame.balloonOn,
      author: tmplFrame.author,
    };
  });
  return tmplFrames.filter(
    (tmplFrame): tmplFrame is Exclude<typeof tmplFrame, undefined> =>
      tmplFrame !== undefined
  );
};

export const makeNoImageTemplteImages = (): ITemplateImage[] => {
  return [...Array(4)].map((_, i) => {
    return {
      id: -1,
      order: i,
      templateID: undefined,
      balloonOff: consts.noImageTemplate,
      balloonOn: consts.noImageTemplate,
      author: { id: -1, name: "", isFamous: false },
    };
  });
};

export const makeSampleFrames = (
  sampleFrames: ISampleFrame[]
): IEditFrame[] => {
  const editFrames = sampleFrames.map((sampleFrame) => {
    return {
      id: undefined,
      editDataID: undefined,
      order: sampleFrame.order,
      templateID: undefined,
      isBalloonOn: sampleFrame.isBalloonOn,
      templateImageID: sampleFrame.templateImageID,
      items: makeSampleItems(sampleFrame.items),
    };
  });
  return editFrames;
};

export const makeSampleItems = (
  sampleItems: IEditableObject[]
): IEditableObject[] => {
  const items = sampleItems;
  items.forEach((item) => {
    item.id = undefined;
    item.editFrameID = undefined;
  });
  return items;
};

// メンテナンス情報モデル
export interface IManageContent {
  id?: number;
  message: string;
}
// 管理者メールアドレスモデル
export interface IMaintainer {
  id?: number;
  email: string;
}

//// 汎用関数lib/sharedに移動する？
export function isText (arg: any): arg is ITextObject {
  return arg.lineHeight !== undefined;
}

export function isImage (arg: any): arg is IImageObject {
  return arg.image !== undefined;
}

export function filterTemplate (
  template: ITemplateSet,
  filter: ITemplateFilter
): boolean {
  if (filter.attributeID !== undefined) {
    // 属性でフィルタ
    const isMatch = template.tags.some((t) => t.id == filter.attributeID);
    if (isMatch) {
      return true;
    }
  }
  if (filter.authorID !== undefined) {
    // 作者でフィルタ
    const isMatch = Array.from(template.frames)
      .map((f) => f.author.id)
      .includes(filter.authorID);
    if (isMatch) {
      return true;
    }
  }
  return false;
}

// 2×2で結合する場合の順序を取得
const getOrder = (x: any, xs: any[]) =>
  x.order === undefined ? xs.indexOf(x) : x.order;
export const twoBytwoOrder = <T> (frames: (ITemplateImage | T)[]) => [
  frames.find((f) => getOrder(f, frames) == 2),
  frames.find((f) => getOrder(f, frames) == 0),
  frames.find((f) => getOrder(f, frames) == 3),
  frames.find((f) => getOrder(f, frames) == 1),
];
export const twoBytwoOrderLTRB = <T> (frames: (ITemplateImage | T)[]) => [
  frames.find((f) => getOrder(f, frames) == 0),
  frames.find((f) => getOrder(f, frames) == 1),
  frames.find((f) => getOrder(f, frames) == 2),
  frames.find((f) => getOrder(f, frames) == 3),
];

// HACKME エラーメッセージ処理のリファクタ
const isError = (ex: any) => {
  return ex.failedField !== undefined;
};
export const isErrors = (ex: any) => {
  if (!Array.isArray(ex)) return false;
  return ex.every((x) => isError(x));
};
export const ErrorTagToMessage = (error: IErrorResponse) => {
  switch (error.tag) {
    case ValidateErrorTags.NotMachPassword:
      return ["パスワードが一致しません"];
    case ValidateErrorTags.PasswordIsInvalid:
      return [
        "パスワードの条件を満たしていません。",
        "英字大文字、英字小文字、数字、記号のうち3種類以上を含む7文字以上20文字以下のパスワードを設定してください。",
      ];
    case ValidateErrorTags.NotEqualPass:
      return ["パスワードが一致しません"];
    case ValidateErrorTags.Required:
      return ["入力必須項目です"];
    case ValidateErrorTags.Email:
      return ["メールアドレス形式で入力してください"];
    case ValidateErrorTags.ExistsSameName:
      return ["同じ名前の契約（会社名＋部署名）が既に存在しています。"];
    case ValidateErrorTags.ExistsSameMail:
      return ["そのメールアドレスは既に使われています"];
    case ValidateErrorTags.GTfield:
      if (error.failedField.includes("Expire")) {
        return ["契約終了日は開始日以降に設定してください"];
      }
      return ["開始日以降に設定してください"];
    case ValidateErrorTags.Lte:
      if (error.failedField.includes("DataURI")) {
        return [`${error.value}byte以上のデータはアップロードできません`];
      } else if (error.failedField.includes("Content")) {
        return [`セリフ入力の文字数を減らすか、分割して再登録してください`];
      } else if (error.failedField.includes("Mail")) {
        return [
          `メールアドレスは${error.value}文字以下の長さで設定してください`,
        ];
      }
      return [
        `${error.failedField}は${error.value}以下の長さで設定してください`,
      ];

    default:
      return ["予期せぬエラーが発生しました。"];
  }
};
export const ErrorMessageFilter = (
  errors: IErrorResponse[],
  fieldName: string
) =>
  errors
    .filter((x) => x.failedField.includes(fieldName))
    .flatMap((x) => ErrorTagToMessage(x));

export const consts = {
  sessionMonitorIntervalMilliseconds: 1000 * 3, // 認証セッションの監視周期(ms)
  maxEditTitleLength: 20, // 編集データのタイトルの最大文字数
  maxEditContentLength: 2048, // 編集オブジェクト(セリフ)の最大文字数
  maxCompanyNameLength: 50, // 契約の会社名の最大文字数
  maxOptionNameLength: 20, // 契約の部署名の最大文字数
  maxContractDescribeLength: 4096, // 契約の備考の最大文字数
  maxEMailLength: 254, // ユーザのメールアドレスの最大文字数
  maxPasswordLength: 20, // ユーザのパスワードの最大文字数
  maxUserNameLength: 20, // ユーザの名前の最大文字数
  plans: [1, 3, 10], // ユーザー数上限
  systemAuthor: 0, // 作者:システムのIDカルーセル様テンプレートを判別するために使用
  senarioAttrGroupID: 5, // シナリオ属性グループID アンケート機能で使用
  checkboxAttrGroupID: 6, //  チェックボックス属性グループID 属性絞り込み機能, テンプレート検索結果一覧機能で使用
  touchAttrGroupID: 4, // タッチ属性グループID アンケート機能, テンプレート選択機能で使用
  sceneAttrGroupID: 2, // シーン属性グループID アンケート機能で使用
  hiddenString: "\u{feff}", // BOMのユニコード表示
  defaultLineHeight: 32, // デフォルトの行間
  defaultFontSize: "36px", // デフォルトのフォントサイズ
  minFontSize: 24, // 最小フォントサイズ
  maxFontSize: 72, // 最大フォントサイズ
  minSerifWidth: "3rem", // セリフの幅の最小値
  minSerifHeight: "24rem", // セリフの高さの最小値
  fonthackResolveMagicValue: "7", // フォントサイズ変更用のマジックナンバー
  fonthackResolveMagicSize: "xxx-large", // フォントサイズ変更用のマジックサイズ
  maxEditDataCount: 1000, // 登録可能な編集データの数
  maxEditDataCountOver: "作業データの登録上限数に達しているため登録できません", // 作業データ登録上限数超過のエラーメッセージ
  maxEditObjectCount: 50, // 登録可能な編集オブジェクトの数
  maxEditObjectCountOver: "登録可能なオブジェクト数を超えています", // 作業オブジェクト配置上限数超過のエラーメッセージ
  maxPasteDataSizeOver: "貼りつけるデータ量または貼りつけ後のデータ量が2048バイトを超えています。", // 編集オブジェクト(セリフ)のペースト文字上限数超過のエラーメッセージ
  exampleWindowSize: { width: 1038, height: 928 }, // 漫画広告サンプル集のウィンドウサイズ
  editModeStopLabel: "契約停止", // 契約停止ダイアログのタイトルとボタンのラベル表示
  editModeRestartLabel: "契約再開", // 契約再開ダイアログのタイトルとボタンのラベル表示
  editModeDeleteLabel: "削除", // 削除ダイアログのタイトルとボタンのラベル表示
  editModeEditLabel: "契約編集", // 契約編集ダイアログのタイトルのラベル表示
  editModeApplyLabel: "適用", // 契約編集ダイアログのボタンのラベル表示
  hiddenStyle: "hidden", // ダイアログを閉じる際に設定するoverflowプロパティの値
  initialSize: { width: 0, height: 0 }, // 編集領域の初期サイズ
  foreColors: [
    { name: "黒", value: "black" },
    { name: "赤", value: "#FF0000FF" },
    { name: "青", value: "#0000FFFF" },
    { name: "緑", value: "#00FF00FF" },
  ], // セリフの文字色プルダウンに設定される値
  backColors: [
    { name: "無色", value: "#00000000" },
    { name: "黄色", value: "#ffff66" },
    { name: "ピンク", value: "#ffccff" },
    { name: "オレンジ", value: "#ffcc99" },
  ], // セリフのマーカー色プルダウンに設定される値
  menuItemCopy: "コピーを作成", // 作業ファイル一覧の各データのメニュー要素名(コピー作成)
  menuItemDelete: "削除", // 作業ファイル一覧の各データのメニュー要素名(削除)
  displayOrderNone: "なし", // ファイルダウンロードの際の読み順番号の配置場所(なし)
  displayOrderCenter: "中心寄せ", // ファイルダウンロードの際の読み順番号の配置場所(中心寄せ)
  displayOrderRightTop: "右上", // ファイルダウンロードの際の読み順番号の配置場所(右上)
  type1URL: "/orders/styleA.svg", // 読み順番号のスタイルのサンプル画像ファイルパス(四角)
  type2URL: "/orders/styleB.svg", // 読み順番号のスタイルのサンプル画像ファイルパス(丸)
  fbSize: { width: 1200, height: 888 }, // Facebook一般向けの1コマのサイズ
  fbadSize: { width: 1080, height: 799 }, // Facebookカルーセル広告向けの1コマのサイズ
  twSize: { width: 1280, height: 948 }, // Twitter向けの1コマのサイズ
  instSize: { width: 1080, height: 799 }, // Instagram向けの1コマのサイズ
  originalSize: { width: 1200, height: 888 }, // 1コマのデフォルトサイズ
  downloadSizeMinScale: 0.2, // その他(%指定)の最小値
  downloadSizeMaxScale: 1.0, // その他(%指定)の最大値
  objectMaxScale: 0.8, // 編集領域においてオブジェクトが占める割合の最大値
  initialPos: { x: 0, y: 0 }, // オブジェクトの座標の初期値
  initialDeg: 0.0, // オブジェクトの角度の初期値
  initialScale: 1.0, // オブジェクトの比率の初期値
  imageLimitLength: 1.0, // 画像の最小幅
  initialPageCount: 1, // 水平スクロールのページ数の初期値
  modDenominator: 1.0, // 全テンプレートを収めるのに必要なページ数を求める計算の分母
  loginFailure: "メールアドレスまたはパスワードが違います", // ログイン失敗時のエラーメッセージ
  adminLoginFailure: "ユーザIDまたはパスワードが違います", // 管理画面ログイン時のエラーメッセージ
  addContractFailure: "契約追加に失敗しました", // 新規契約の追加失敗時のエラーメッセージ
  updateContractFailure: "契約更新に失敗しました", // 既存契約の更新失敗時のエラーメッセージ
  changePasswordFailure: "パスワード再設定に失敗しました。", // パスワード変更失敗時のエラーメッセージ
  resetPasswordFailure: "パスワードリセットに失敗しました。", // パスワードリセット失敗時のエラーメッセージ
  uploadMaterialFailure: "素材のアップロードに失敗しました", // 素材アップロード失敗時のエラーメッセージ
  uploadMaterialNotImage: "画像ファイルを指定してください", // アップロード画像の形式不正時のエラーメッセージ
  uploadMaterialSizeError: "画像ファイルを読み込めませんでした", // アップロード画像の最大サイズを超過していた際のエラーメッセージ
  saveEditDataFailure: "作業データの登録に失敗しました", // 作業データの保存失敗時のエラーメッセージ
  updateMaintainersMailFailure: "管理者メールアドレスの更新に失敗しました", // 管理者メールアドレスの更新失敗時のエラーメッセージ
  moveConfirmMessage:
    "サイトから移動しますか？\n変更内容が保存されない可能性があります。", // 変更が保存されていない状態で画面遷移しようとした場合のエラーメッセージ
  adminContractCreateLabel: "契約管理画面", // 管理画面の契約管理ボタンを押下した際の遷移先のタイトルラベル
  adminMaintenanceLabel: "メンテナンス情報登録画面", // 管理画面のメンテナンスボタンを押下した際の遷移先のタイトルラベル
  adminUsersLabel: "管理者情報画面", // 管理画面の管理者情報ボタンを押下した際の遷移先のタイトルラベル
  adminContractUsersLabel: "ユーザー管理画面", // 契約管理画面のユーザー管理ボタンを押下した際の遷移先のタイトルラベル
  defaultQuality: 1.0, // データURIのデフォルトの品質
  expireOneMonth: 31, // 1か月を日付に換算した値
  // フォントサイズと行間の設定
  fontSizeToLineHeight: {
    "24px": 14,
    "26px": 17,
    "28px": 20,
    "30px": 23,
    "32px": 26,
    "34px": 29,
    "36px": 32,
    "38px": 35,
    "40px": 37,
    "42px": 40,
    "44px": 42,
    "46px": 45,
    "48px": 48,
    "50px": 51,
    "52px": 53,
    "54px": 56,
    "56px": 58,
    "58px": 61,
    "60px": 64,
    "62px": 67,
    "64px": 70,
    "66px": 73,
    "68px": 76,
    "70px": 79,
    "72px": 82,
  },
  noImageThumbnail: "img/template/noimage_thumbnail.png", // サムネイルなし画像
  noImageTemplate: "img/template/noimage.png", // テンプレートなし画像
  topBgText: "img/bgText/bg_text.png", // トップページ表示画像
  helpPdf: "help/Mangafield_help.pdf", // ヘルプPDFファイル
};

// TODO APIモデルじゃないやつは移動する
export interface IQuestionnaireData {
  title: string;
  message: string;
  choices: {
    text: string;
    choiceID: number;
  }[];
}
// 出力形式
export enum OutputType {
  vAlign,
  split,
  twoBytwo,
  splitBySelect,
}
// 読み順番号の位置
export enum DisplayOrder {
  none,
  center,
  rightTop,
}
// 読み順番号のスタイル
export enum OrderStyle {
  type1,
  type2,
  type3,
}
// 読み順
export enum ViewOrder {
  RTLB,
  LTRB,
}
// 2×2で結合時のオプション
export interface I2x2Option {
  order: ViewOrder;
  displayOrder: DisplayOrder;
  orderStyle: OrderStyle;
}
// 出力形式インターフェース
export interface IOutputStyle {
  outputType: OutputType;
  selectFrames: number[];
  twoBytwoOption: I2x2Option;
}

export interface LogInfoBase {
  action: string;
}
// アンケート結果
export interface SelectedQuestionnaires extends LogInfoBase {
  options: {
    choices: {
      message: string;
      selectedText: string;
    }[];
  };
}
// 属性選択状態
export interface SelectedAttribute extends LogInfoBase {
  options: {
    choices: {
      attributeGroup: string;
      selectedAttributes: string[];
    }[];
  };
}

export interface SelectedTemplate extends LogInfoBase {
  options: {
    templateId: number;
  };
}

export interface SaveTemplate extends LogInfoBase {
  options: {
    templateId: number;
  };
}
